import {formatDate} from '@angular/common';
import {Injectable} from '@angular/core';
import {jsPDF, jsPDFOptions} from 'jspdf';
import {formatKpiTile} from '@features/admin-panel/components/admin-dashboard/kpi-tile/admin-dashboard-kpi-tile.format';
import {AdminDashboardKpi} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportColumn,
  AdminDashboardReportHeader,
  AdminDashboardReportRow,
  AdminDashboardReportSection,
  AdminDashboardReportStat
} from '@features/admin-panel/models/admin-dashboard-report.models';

/** jsPDF behind a plain object, so a spec can stub it: a module export is read-only and cannot be spied on. */
export const reportPdf = {
  create: (options: jsPDFOptions): jsPDF => new jsPDF(options)
};

/** The report's colours: the OCA-E-159 light tokens. */
export const REPORT_THEME = {
  /** standard-0 */
  ink: '#101214',
  /** standard-600 */
  ink2: '#596773',
  /** standard-700 */
  ink3: '#738496',
  /** standard-1000 */
  border: '#DEE4EA',
  /** standard-1100 */
  band: '#EAEEF3',
  /** Every second table row (the band at 50%). */
  zebra: '#F5F7F9',
  white: '#FFFFFF',
  /** blue-0 */
  accent: '#175CFF',
  statusText: {red: '#9B1942', amber: '#7A4D0B', green: '#176448'},
  statusBg: {red: '#FFC8D7', amber: '#FFF3CD', green: '#CBF5E5'}
};

/** The glyphs the PDF's built-in Helvetica (WinAnsi encoding) cannot draw, and what the report writes instead. */
const GLYPHS: [RegExp, string][] = [
  [/▲\s?/g, '+'],
  [/▼\s?/g, '-'],
  [/→/g, '->'],
  [/←/g, '<-'],
  [/≥/g, '>='],
  [/≤/g, '<='],
  [/−/g, '-']
];

/** The WinAnsi characters above U+00FF; every character up to U+00FF is drawn as it is. */
const WIN_ANSI_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';

/**
 * Text the built-in PDF font can draw: "▲ 3.2%" becomes "+3.2%", "▼ 3.2%" "-3.2%", arrows and comparison signs are
 * spelled out, and any other character outside WinAnsi becomes "?".
 */
export function pdfSafeText(text: string): string {
  const spelled = GLYPHS.reduce((value, [pattern, glyph]) => value.replace(pattern, glyph), text ?? '');
  return Array.from(spelled)
    .map(ch => ch.charCodeAt(0) <= 0xFF || WIN_ANSI_EXTRA.includes(ch) ? ch : '?')
    .join('');
}

/** The family the report draws in once the Liberation Sans files are registered with the document. */
export const REPORT_FONT = 'Liberation';

/** The built-in family the report falls back to when the font files cannot be loaded (with pdfSafeText). */
const FALLBACK_FONT = 'helvetica';

/**
 * The Liberation Sans Regular and Bold files (SIL OFL; pdfjs-dist's standard_fonts, copied to assets/report-fonts by
 * angular.json), as base64, behind a plain object so a spec can stub the fetch.
 */
export const reportFonts = {
  load: async (): Promise<{regular: string; bold: string}> => {
    const [regular, bold] = await Promise.all([
      fetchBase64('assets/report-fonts/LiberationSans-Regular.ttf'),
      fetchBase64('assets/report-fonts/LiberationSans-Bold.ttf')
    ]);
    return {regular, bold};
  }
};

async function fetchBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${url}: HTTP ${response.status}`);
  }
  return toBase64(new Uint8Array(await response.arrayBuffer()));
}

/** Base64 of the bytes, 32 KB at a time (String.fromCharCode cannot take a whole font file as arguments). */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
}

/**
 * The code points Liberation Sans draws: Latin (with Extended-A/B and Extended Additional), Greek, Cyrillic, general
 * punctuation, currency, letterlike symbols, arrows and geometric shapes (▲ ▼).
 */
const FONT_RANGES: [number, number][] = [
  [0x0000, 0x024F], [0x0370, 0x03FF], [0x0400, 0x052F], [0x1E00, 0x1EFF], [0x2000, 0x206F],
  [0x20A0, 0x20BF], [0x2100, 0x214F], [0x2190, 0x21FF], [0x25A0, 0x25FF]
];

/** True when any character is outside what the report font draws (CJK, Arabic, Thai, …): such text becomes a picture. */
export function needsCanvasText(text: string): boolean {
  return Array.from(text ?? '').some(ch => {
    const code = ch.codePointAt(0);
    return !FONT_RANGES.some(([from, to]) => code >= from && code <= to);
  });
}

/** Text pictures are drawn at 4x, in the browser's own fonts (which do cover CJK and the other scripts). */
const CANVAS_SCALE = 4;
const CANVAS_FONTS = 'Geist, Inter, system-ui, "Segoe UI", "Noto Sans CJK SC", "Microsoft YaHei", "Yu Gothic", sans-serif';

/**
 * One line of text as a PNG: cut with "…" to `maxWidthPt` (measured on the canvas), its baseline 0.8 x the size below
 * the picture's top. Width and height are in points. Null when the browser has no 2D canvas.
 */
export function renderCanvasText(text: string, sizePt: number, color: string, bold: boolean,
                                 maxWidthPt: number): {image: string; width: number; height: number} | null {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  const font = `${bold ? 600 : 400} ${sizePt * CANVAS_SCALE}px ${CANVAS_FONTS}`;
  context.font = font;
  const fitted = fitCanvasText(context, text ?? '', maxWidthPt * CANVAS_SCALE);
  const widthPx = Math.max(1, Math.ceil(context.measureText(fitted).width));
  const heightPx = Math.ceil(sizePt * CANVAS_SCALE * 1.2);
  canvas.width = widthPx;
  canvas.height = heightPx;
  // Resizing a canvas resets its drawing state.
  context.font = font;
  context.fillStyle = color;
  context.textBaseline = 'alphabetic';
  context.fillText(fitted, 0, sizePt * CANVAS_SCALE * 0.8);
  return {image: canvas.toDataURL('image/png'), width: widthPx / CANVAS_SCALE, height: heightPx / CANVAS_SCALE};
}

/** The text cut with "…" until it is no wider than `maxWidth` canvas pixels. */
function fitCanvasText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (context.measureText(text).width <= maxWidth) {
    return text;
  }
  const chars = Array.from(text);
  while (chars.length && context.measureText(chars.join('') + '…').width > maxWidth) {
    chars.pop();
  }
  return chars.join('').replace(/\s+$/, '') + '…';
}

// A4 portrait, in points.
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
/** Content stops here; the 48 pt below is the footer zone. */
const CONTENT_BOTTOM = PAGE_HEIGHT - 48;
const FOOTER_LINE_Y = 812;
const RULE_WIDTH = 0.75;
const KPI_COLUMNS = 4;
const KPI_GAP = 8;
const KPI_HEIGHT = 72;
const SECTION_GAP = 20;
const SECTION_MIN_SPACE = 140;
const BLOCK_GAP = 12;
const CARD_PADDING = 12;
const CARD_RADIUS = 6;
const INNER_X = MARGIN + CARD_PADDING;
const INNER_WIDTH = CONTENT_WIDTH - 2 * CARD_PADDING;
const BLOCK_TITLE_HEIGHT = 14;
const BLOCK_SUBTITLE_HEIGHT = 12;
const BLOCK_HEADER_GAP = 6;
const BODY_LINE = 12;
const NOTE_GAP = 4;
const NOTE_LINE = 10;
const ROW_HEIGHT = 15;
const CELL_PADDING = 4;
const STAT_COLUMNS = 4;
const STAT_GAP = 8;
const STAT_HEIGHT = 44;
const CHART_MAX_HEIGHT = 230;

type TableBlock = Extract<AdminDashboardReportBlock, {kind: 'table'}>;
type ChartBlock = Extract<AdminDashboardReportBlock, {kind: 'chart'}>;

/** "Clients · Last 30 days · No filters · Generated 4 Oct 2026, 23:30 UTC" */
function metaLine(header: AdminDashboardReportHeader): string {
  const generated = formatDate(header.generatedAt, 'd MMM y, HH:mm', 'en-US', 'UTC');
  return `${header.scopeLabel} · ${header.periodLabel} · ${header.filtersLabel} · Generated ${generated} UTC`;
}

/** Each column's width: its share (weight, default 1) of the table width. */
function columnWidths(columns: AdminDashboardReportColumn[], width: number): number[] {
  const weights = columns.map(column => column.weight ?? 1);
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  return weights.map(weight => width * weight / total);
}

/**
 * Lays one report out top to bottom. `y` is the cursor on the current page; whatever does not fit above the footer
 * zone goes onto a new page.
 */
class ReportWriter {
  private y = MARGIN;
  /** The current font, for text drawn as a picture. */
  private size = 10;
  private color: string = REPORT_THEME.ink;
  private bold = false;

  constructor(private readonly doc: jsPDF, private readonly fontFamily: string) {}

  /** Page 1: the accent bar, the title, the meta line and the KPI grid. */
  drawFirstPage(header: AdminDashboardReportHeader, kpis: AdminDashboardKpi[]): void {
    this.doc.setFillColor(REPORT_THEME.accent);
    this.doc.rect(0, 0, PAGE_WIDTH, 4, 'F');
    this.font(20, REPORT_THEME.ink, true);
    this.drawText('Admin Panel — Dashboard', MARGIN, 56, CONTENT_WIDTH);
    this.font(9, REPORT_THEME.ink2);
    this.drawText(metaLine(header), MARGIN, 72, CONTENT_WIDTH);
    this.y = 88;
    this.drawKpis(kpis);
  }

  /** A section: on a new page when fewer than 140 pt remain, else 20 pt under what came before; then its blocks. */
  drawSection(section: AdminDashboardReportSection): void {
    if (CONTENT_BOTTOM - this.y < SECTION_MIN_SPACE) {
      this.newPage();
    } else {
      this.y += SECTION_GAP;
    }
    this.font(14, REPORT_THEME.ink, true);
    this.y += 14;
    this.drawText(section.title, MARGIN, this.y, CONTENT_WIDTH);
    if (section.subtitle) {
      this.font(9, REPORT_THEME.ink2);
      this.y += 13;
      this.drawText(section.subtitle, MARGIN, this.y, CONTENT_WIDTH);
    }
    this.y += 8;
    this.rule(this.y);
    this.y += 12;
    section.blocks.forEach(block => this.drawBlock(block));
  }

  /** On every page: a rule, "Admin Panel dashboard · <scope> · <period>" on the left and "Page X of Y" on the right. */
  drawFooters(header: AdminDashboardReportHeader): void {
    const pages = this.doc.getNumberOfPages();
    for (let page = 1; page <= pages; page++) {
      this.doc.setPage(page);
      this.rule(FOOTER_LINE_Y);
      this.font(7.5, REPORT_THEME.ink3);
      this.drawText(`Admin Panel dashboard · ${header.scopeLabel} · ${header.periodLabel}`, MARGIN, FOOTER_LINE_Y + 12, CONTENT_WIDTH);
      this.drawText(`Page ${page} of ${pages}`, MARGIN + CONTENT_WIDTH, FOOTER_LINE_Y + 12, CONTENT_WIDTH, 'right');
    }
  }

  /** The KPI cards, four to a row, each with the label, value and detail its tile shows on screen (the detail on up to two lines). */
  private drawKpis(kpis: AdminDashboardKpi[]): void {
    if (!kpis.length) {
      return;
    }
    const width = (CONTENT_WIDTH - (KPI_COLUMNS - 1) * KPI_GAP) / KPI_COLUMNS;
    kpis.forEach((kpi, index) => {
      const x = MARGIN + (index % KPI_COLUMNS) * (width + KPI_GAP);
      const top = this.y + Math.floor(index / KPI_COLUMNS) * (KPI_HEIGHT + KPI_GAP);
      const tile = formatKpiTile(kpi);
      this.doc.setDrawColor(REPORT_THEME.border);
      this.doc.setLineWidth(RULE_WIDTH);
      this.doc.setFillColor(REPORT_THEME.white);
      this.doc.roundedRect(x, top, width, KPI_HEIGHT, CARD_RADIUS, CARD_RADIUS, 'FD');
      this.font(8, REPORT_THEME.ink2);
      this.drawText(kpi.label, x + 10, top + 16, width - 20);
      this.font(16, REPORT_THEME.ink, true);
      this.drawText(tile.value, x + 10, top + 38, width - 20);
      if (tile.detail) {
        this.font(8, REPORT_THEME.ink3);
        this.kpiDetailLines(tile.detail, width - 20)
          .forEach((line, index) => this.drawText(line, x + 10, top + 52 + index * 10, width - 20));
      }
    });
    const rows = Math.ceil(kpis.length / KPI_COLUMNS);
    this.y += rows * KPI_HEIGHT + (rows - 1) * KPI_GAP;
  }

  /**
   * The KPI detail as at most two lines of `width` in the current font: the first line as wrapped, the second the
   * rest of the text (drawText cuts it with "…"). Text drawn as a picture stays one line.
   */
  private kpiDetailLines(detail: string, width: number): string[] {
    if (needsCanvasText(detail)) {
      return [detail];
    }
    const safe = this.safe(detail);
    const first = ((this.doc.splitTextToSize(safe, width) as string[])[0] ?? '').replace(/\s+$/, '');
    const rest = safe.slice(first.length).trim();
    return rest ? [first, rest] : [first];
  }

  /** One block as a card: its header, its body and its note. A table with rows may run over several pages. */
  private drawBlock(block: AdminDashboardReportBlock): void {
    if (block.kind === 'table' && block.rows.length) {
      this.drawTable(block);
    } else {
      const height = 2 * CARD_PADDING + this.headerHeight(block.subtitle) + this.bodyHeight(block) + this.noteHeight(block.note);
      this.ensureSpace(height);
      const top = this.y;
      this.y += CARD_PADDING;
      this.drawBlockHeader(block.title, block.subtitle);
      this.drawBody(block);
      this.drawNote(block.note);
      this.y = top + height;
      this.outline(top, height);
    }
    this.y += BLOCK_GAP;
  }

  /** The height of a block's body (a table here has no rows: its body is its empty text). */
  private bodyHeight(block: AdminDashboardReportBlock): number {
    switch (block.kind) {
      case 'table':
        return this.wrap(block.emptyText ?? 'No rows', 9).length * BODY_LINE;
      case 'stats':
        return this.statsHeight(block.stats.length);
      case 'chart': {
        const size = this.chartSize(block);
        return size ? size.height : this.wrap(block.emptyText ?? 'No data', 9).length * BODY_LINE;
      }
      case 'empty':
        return this.wrap(block.text, 9).length * BODY_LINE;
      default:
        return 0;
    }
  }

  private drawBody(block: AdminDashboardReportBlock): void {
    switch (block.kind) {
      case 'table':
        this.drawEmpty(block.emptyText ?? 'No rows');
        break;
      case 'stats':
        this.drawStats(block.stats);
        break;
      case 'chart':
        this.drawChart(block);
        break;
      case 'empty':
        this.drawEmpty(block.text);
        break;
    }
  }

  /**
   * A table card. When the next row does not fit, the card is closed, a new page starts and the card goes on under
   * "<title> (continued)" with the header row drawn again.
   */
  private drawTable(block: TableBlock): void {
    const widths = columnWidths(block.columns, INNER_WIDTH);
    const noteHeight = this.noteHeight(block.note);
    let index = 0;
    let continued = false;
    while (index < block.rows.length) {
      const subtitle = continued ? undefined : block.subtitle;
      this.ensureSpace(2 * CARD_PADDING + this.headerHeight(subtitle) + 2 * ROW_HEIGHT + noteHeight);
      const top = this.y;
      this.y += CARD_PADDING;
      this.drawBlockHeader(continued ? `${block.title} (continued)` : block.title, subtitle);
      this.drawTableHeader(block.columns, widths);
      const first = index;
      while (index < block.rows.length) {
        const last = index === block.rows.length - 1;
        if (index > first && this.y + ROW_HEIGHT + (last ? noteHeight : 0) + CARD_PADDING > CONTENT_BOTTOM) {
          break;
        }
        this.drawTableRow(block.rows[index], block.columns, widths, index);
        index++;
      }
      if (index === block.rows.length) {
        this.drawNote(block.note);
      }
      this.y += CARD_PADDING;
      this.outline(top, this.y - top);
      if (index < block.rows.length) {
        this.newPage();
        continued = true;
      }
    }
  }

  /** The header row: band fill, 8 pt bold ink2. */
  private drawTableHeader(columns: AdminDashboardReportColumn[], widths: number[]): void {
    this.doc.setFillColor(REPORT_THEME.band);
    this.doc.rect(INNER_X, this.y, INNER_WIDTH, ROW_HEIGHT, 'F');
    this.font(8, REPORT_THEME.ink2, true);
    this.drawCells(columns.map(column => column.header), columns, widths);
    this.y += ROW_HEIGHT;
  }

  /** One row: zebra fill on every second row, a 3 pt status stripe when it has a tone, 8.5 pt ink text. */
  private drawTableRow(row: AdminDashboardReportRow, columns: AdminDashboardReportColumn[], widths: number[], index: number): void {
    if (index % 2 === 1) {
      this.doc.setFillColor(REPORT_THEME.zebra);
      this.doc.rect(INNER_X, this.y, INNER_WIDTH, ROW_HEIGHT, 'F');
    }
    if (row.tone) {
      this.doc.setFillColor(REPORT_THEME.statusText[row.tone]);
      this.doc.rect(INNER_X, this.y, 3, ROW_HEIGHT, 'F');
    }
    this.font(8.5, REPORT_THEME.ink);
    this.drawCells(row.cells, columns, widths);
    this.y += ROW_HEIGHT;
  }

  /** Each cell cut to its column with "…", left- or right-aligned. */
  private drawCells(cells: string[], columns: AdminDashboardReportColumn[], widths: number[]): void {
    let x = INNER_X;
    columns.forEach((column, index) => {
      const width = widths[index] - 2 * CELL_PADDING;
      if (column.align === 'right') {
        this.drawText(cells[index] ?? '', x + widths[index] - CELL_PADDING, this.y + 10.5, width, 'right');
      } else {
        this.drawText(cells[index] ?? '', x + CELL_PADDING, this.y + 10.5, width);
      }
      x += widths[index];
    });
  }

  /** Stat tiles, four to a row: label (8 ink2), value (14 bold), detail (8 ink3) on a band-filled tile. */
  private drawStats(stats: AdminDashboardReportStat[]): void {
    const width = (INNER_WIDTH - (STAT_COLUMNS - 1) * STAT_GAP) / STAT_COLUMNS;
    stats.forEach((stat, index) => {
      const x = INNER_X + (index % STAT_COLUMNS) * (width + STAT_GAP);
      const top = this.y + Math.floor(index / STAT_COLUMNS) * (STAT_HEIGHT + STAT_GAP);
      this.doc.setFillColor(REPORT_THEME.band);
      this.doc.roundedRect(x, top, width, STAT_HEIGHT, 4, 4, 'F');
      this.font(8, REPORT_THEME.ink2);
      this.drawText(stat.label, x + 8, top + 12, width - 16);
      this.font(14, REPORT_THEME.ink, true);
      this.drawText(stat.value, x + 8, top + 29, width - 16);
      if (stat.detail) {
        this.font(8, REPORT_THEME.ink3);
        this.drawText(stat.detail, x + 8, top + 40, width - 16);
      }
    });
    this.y += this.statsHeight(stats.length);
  }

  private statsHeight(count: number): number {
    const rows = Math.ceil(count / STAT_COLUMNS);
    return rows ? rows * STAT_HEIGHT + (rows - 1) * STAT_GAP : 0;
  }

  /** The chart picture, centred, or its empty text when there is no picture. */
  private drawChart(block: ChartBlock): void {
    const size = this.chartSize(block);
    if (!size) {
      this.drawEmpty(block.emptyText ?? 'No data');
      return;
    }
    this.doc.addImage(block.image, 'PNG', INNER_X + (INNER_WIDTH - size.width) / 2, this.y, size.width, size.height, undefined, 'FAST');
    this.y += size.height;
  }

  /** The picture's size: the card's inside width at the picture's aspect ratio, at most 230 pt high; null without one. */
  private chartSize(block: ChartBlock): {width: number; height: number} | null {
    if (!block.image || !block.widthPx || !block.heightPx) {
      return null;
    }
    let width = INNER_WIDTH;
    let height = width * block.heightPx / block.widthPx;
    if (height > CHART_MAX_HEIGHT) {
      height = CHART_MAX_HEIGHT;
      width = height * block.widthPx / block.heightPx;
    }
    return {width, height};
  }

  /** The block's title (11 pt bold) and, when it has one, its subtitle (8.5 pt ink2). */
  private drawBlockHeader(title: string, subtitle?: string): void {
    this.font(11, REPORT_THEME.ink, true);
    this.drawText(title, INNER_X, this.y + 11, INNER_WIDTH);
    this.y += BLOCK_TITLE_HEIGHT;
    if (subtitle) {
      this.font(8.5, REPORT_THEME.ink2);
      this.drawText(subtitle, INNER_X, this.y + 9, INNER_WIDTH);
      this.y += BLOCK_SUBTITLE_HEIGHT;
    }
    this.y += BLOCK_HEADER_GAP;
  }

  private headerHeight(subtitle?: string): number {
    return BLOCK_TITLE_HEIGHT + (subtitle ? BLOCK_SUBTITLE_HEIGHT : 0) + BLOCK_HEADER_GAP;
  }

  /** A card's own sentence (9 pt ink3), wrapped to the card. */
  private drawEmpty(text: string): void {
    const lines = this.wrap(text, 9);
    this.font(9, REPORT_THEME.ink3);
    lines.forEach((line, index) => this.drawText(line, INNER_X, this.y + 9 + index * BODY_LINE, INNER_WIDTH));
    this.y += lines.length * BODY_LINE;
  }

  /** The block's note under its body (8 pt ink3), e.g. "Collecting history — trend available from 3 Oct 2026". */
  private drawNote(note?: string): void {
    if (!note) {
      return;
    }
    const lines = this.wrap(note, 8);
    this.font(8, REPORT_THEME.ink3);
    this.y += NOTE_GAP;
    lines.forEach((line, index) => this.drawText(line, INNER_X, this.y + 8 + index * NOTE_LINE, INNER_WIDTH));
    this.y += lines.length * NOTE_LINE;
  }

  private noteHeight(note?: string): number {
    return note ? NOTE_GAP + this.wrap(note, 8).length * NOTE_LINE : 0;
  }

  /** The text in lines as wide as the card's inside, at that size of the regular font. */
  private wrap(text: string, size: number): string[] {
    this.doc.setFont(this.fontFamily, 'normal');
    this.doc.setFontSize(size);
    return this.doc.splitTextToSize(this.safe(text), INNER_WIDTH) as string[];
  }

  /** The text on one line of that width at the current font, cut with "…" when it is longer. */
  private fit(text: string, width: number): string {
    const safe = this.safe(text);
    if (this.doc.getTextWidth(safe) <= width) {
      return safe;
    }
    const lines = this.doc.splitTextToSize(safe, Math.max(1, width - this.doc.getTextWidth('…'))) as string[];
    return (lines[0] ?? '').replace(/\s+$/, '') + '…';
  }

  /**
   * One line of text at (x, baseline y) in the current font, cut to `width` with "…"; `align: 'right'` puts its right
   * edge at x. Text the report font cannot draw (CJK and other scripts) is drawn as a picture instead.
   */
  private drawText(text: string, x: number, y: number, width: number, align: 'left' | 'right' = 'left'): void {
    if (needsCanvasText(text)) {
      this.drawCanvasText(text, x, y, width, align);
      return;
    }
    const fitted = this.fit(text, width);
    if (align === 'right') {
      this.doc.text(fitted, x, y, {align: 'right'});
    } else {
      this.doc.text(fitted, x, y);
    }
  }

  /** The text as a 4x canvas picture in the current size, colour and weight, its baseline on y. */
  private drawCanvasText(text: string, x: number, y: number, width: number, align: 'left' | 'right'): void {
    const picture = renderCanvasText(text, this.size, this.color, this.bold, width);
    if (!picture) {
      return;
    }
    const left = align === 'right' ? x - picture.width : x;
    this.doc.addImage(picture.image, 'PNG', left, y - this.size * 0.8, picture.width, picture.height, undefined, 'FAST');
  }

  /** The text as the document's font can draw it: as is in Liberation Sans, through pdfSafeText in the Helvetica fallback. */
  private safe(text: string): string {
    return this.fontFamily === REPORT_FONT ? (text ?? '') : pdfSafeText(text);
  }

  private font(size: number, color: string, bold = false): void {
    this.size = size;
    this.color = color;
    this.bold = bold;
    this.doc.setFont(this.fontFamily, bold ? 'bold' : 'normal');
    this.doc.setFontSize(size);
    this.doc.setTextColor(color);
  }

  /** A 0.75 pt border-coloured line across the content width. */
  private rule(y: number): void {
    this.doc.setDrawColor(REPORT_THEME.border);
    this.doc.setLineWidth(RULE_WIDTH);
    this.doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  }

  /** A block card's rounded border, drawn once its content is on the page. */
  private outline(top: number, height: number): void {
    this.doc.setDrawColor(REPORT_THEME.border);
    this.doc.setLineWidth(RULE_WIDTH);
    this.doc.roundedRect(MARGIN, top, CONTENT_WIDTH, height, CARD_RADIUS, CARD_RADIUS, 'S');
  }

  /** A new page when `height` does not fit above the footer zone. */
  private ensureSpace(height: number): void {
    if (this.y + height > CONTENT_BOTTOM) {
      this.newPage();
    }
  }

  private newPage(): void {
    this.doc.addPage('a4', 'portrait');
    this.y = MARGIN;
  }
}

/**
 * The Admin dashboard as a designed A4 report (ITP-E-146), drawn as vector from the dashboard's own data: page 1 with
 * the KPI grid, then each section's card blocks, then a footer on every page. Replaces ITP-E-141's screenshot PDF.
 */
@Injectable({providedIn: 'root'})
export class AdminDashboardReportPdfService {
  /** The Liberation Sans files, base64, kept after the first export that loaded them. */
  private fonts: {regular: string; bold: string} | null = null;

  /** Builds and saves the report; resolves the file name. */
  async export(header: AdminDashboardReportHeader, kpis: AdminDashboardKpi[], sections: AdminDashboardReportSection[]): Promise<string> {
    const doc = reportPdf.create({unit: 'pt', format: 'a4', orientation: 'portrait', compress: true});
    const writer = new ReportWriter(doc, await this.registerFonts(doc));
    writer.drawFirstPage(header, kpis ?? []);
    (sections ?? []).forEach(section => writer.drawSection(section));
    writer.drawFooters(header);
    const fileName = `admin-dashboard-${header.generatedAt.toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    return fileName;
  }

  /**
   * Registers Liberation Sans (normal and bold) with the document and returns its family; when the files cannot be
   * loaded, warns and returns the built-in Helvetica, so the export still works (with pdfSafeText's substitutions).
   */
  private async registerFonts(doc: jsPDF): Promise<string> {
    try {
      this.fonts = this.fonts ?? await reportFonts.load();
      doc.addFileToVFS('LiberationSans-Regular.ttf', this.fonts.regular);
      doc.addFont('LiberationSans-Regular.ttf', REPORT_FONT, 'normal');
      doc.addFileToVFS('LiberationSans-Bold.ttf', this.fonts.bold);
      doc.addFont('LiberationSans-Bold.ttf', REPORT_FONT, 'bold');
      return REPORT_FONT;
    } catch (error) {
      console.warn('Admin dashboard report: the report font could not be loaded; using Helvetica.', error);
      return FALLBACK_FONT;
    }
  }
}
